import { skipToken, useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { getDeals } from 'src/api';
import { dealsVendorAtom, vendorHasDealsAtom } from 'src/store';
import { CartGetData } from 'src/types';

export const useVendorDeals = ({ cartId }: { cartId?: string }) => {
  const setDealsVendor = useSetAtom(dealsVendorAtom);
  const setVendorHasDeals = useSetAtom(vendorHasDealsAtom);

  // Observer-only: reads from cache populated by useGetCartQuery, never triggers its own fetch
  const { data: cartData } = useQuery<CartGetData>({
    queryKey: ['cart', cartId ?? ''],
    queryFn: skipToken,
    staleTime: Infinity,
  });

  const { data: deals } = useQuery({
    queryKey: ['deals'],
    queryFn: getDeals,
    staleTime: 1000 * 60 * 10,
    enabled: !!cartId,
  });

  useEffect(() => {
    const vendor = cartData?.vendor;
    const vendorDisplayName = cartData?.vendorDisplayName;
    if (!cartId || !vendor || !vendorDisplayName || !deals) {
      setDealsVendor(null);
      setVendorHasDeals(false);
      return;
    }
    const hasDeals = deals.some((deal) => deal.retailer === vendor);
    setDealsVendor(vendorDisplayName);
    setVendorHasDeals(hasDeals);
  }, [cartId, cartData?.vendor, cartData?.vendorDisplayName, deals, setDealsVendor, setVendorHasDeals]);

  // Reset atoms when the cart page unmounts
  useEffect(() => {
    return () => {
      setDealsVendor(null);
      setVendorHasDeals(false);
    };
  }, [setDealsVendor, setVendorHasDeals]);
};
